/* Importing stylesheets */
import "./brioche.css";
import {
  BRIOCHE_DEFAULT_ARGS,
  BRIOCHE_PART_FORMATTING_CLASSES_MAP,
  BRIOCHE_POSITION_CLASSES_MAP,
  BRIOCHE_ROOT_SELECTOR,
  BRIOCHE_WRAPPER_SELECTOR
} from "./Brioche.constant";
import { createRoot, Root } from "react-dom/client";
import { Brioche } from "./Brioche";
import { BriocheDisplay } from "./BriocheDisplay";
import { BriocheArgs } from "./BriocheArgs";
import { BriocheSettings } from "./BriocheSettings";
import { SwipeDirection } from "../../lib/useDismissSwipe/SwipeDirection";

/**
 * Brioche is basically a custom toast.
 * It should display short messages with optional icons and controls.
 *
 * This is the controlling class for all Brioche components in the app.
 * The messages are queued and get shown after each other.
 */
class BriocheEngine implements BriocheDisplay {
  private readonly key = `${Date.now().toString(10)}_${Math.floor(Math.random() * 10000).toString(10)}`;
  private readonly queue: BriocheSettings[] = [];
  /**
   * Status flag. `true` during the whole rendering process, including
   * animating in and animating out.
   * @private
   */
  private isRendering: boolean = false;
  /**
   * Registered async functions that need to be canceled on dismissal
   * @private
   */
  private timeoutsToBeDismissed: number[] = [];
  private _isDismissable: boolean = false;
  /**
   * Status flag. `true` when the Brioche can be dismissed. E.g.: showing
   * a message, but not animating in or out.
   */
  private get isDismissable() {
    return this._isDismissable;
  }
  private set isDismissable(value: boolean) {
    if (this._isDismissable === value) {
      return;
    }
    this._isDismissable = value;
    this.timeoutsToBeDismissed = [];
  }

  private activeSettings?: BriocheSettings;

  private readonly root: Root;

  private readonly rootElem: HTMLDivElement;
  private wrapperElement?: Element;

  private _debug: boolean = false;
  private timerCheck: number = 0;

  public constructor() {
    this.rootElem = document.createElement("div");
    this.rootElem.setAttribute("id", this.key);
    this.rootElem.setAttribute(BRIOCHE_ROOT_SELECTOR, "");

    this.root = createRoot(this.rootElem);
  }

  /**
   * Show the elapsed time since last timer action with custom text messages.
   * Works only in debug mode.
   * @param args
   */
  private readonly showTimer = (...args: string[]) => {
    const lastTimer = this.timerCheck;
    this.timerCheck = performance.now();
    if (!this._debug) {
      return;
    }
    console.debug(...args, `${(this.timerCheck - lastTimer).toFixed(1)}ms`);
  };

  /**
   * Selects the actually preferred wrapper element and append the Brioche root.
   * This might come handy if certain elements are wrapped in something, but
   * Brioche should slide behind them.
   * @private
   */
  private placeRootIntoWrapper() {
    const currentWrapper =
      document.body.querySelector(BRIOCHE_WRAPPER_SELECTOR) ?? document.body;
    if (this.wrapperElement && this.wrapperElement !== currentWrapper) {
      this.wrapperElement.removeChild(this.rootElem);
      this.wrapperElement = undefined;
    }
    if (!this.wrapperElement) {
      this.wrapperElement = currentWrapper;
      this.wrapperElement.appendChild(this.rootElem);
    }
  }

  private addToQueue(settings: BriocheSettings) {
    this.queue.push(settings);
    this.render();
  }

  private argsToSettings(args: BriocheArgs): BriocheSettings {
    return {
      ...BRIOCHE_DEFAULT_ARGS,
      ...BRIOCHE_POSITION_CLASSES_MAP[
        args.position ?? BRIOCHE_DEFAULT_ARGS.position
      ],
      ...BRIOCHE_PART_FORMATTING_CLASSES_MAP[
        args.theme ?? BRIOCHE_DEFAULT_ARGS.theme
      ],
      ...args
    };
  }

  /**
   * This function initiates the animation on deactivation
   * @private
   */
  private createDeactivatorFn() {
    return () => {
      if (!this.activeSettings) {
        throw new Error("No active settings are present");
      }
      // Moving out animation started, this Brioche is not dismissable anymore
      this.isDismissable = false;
      const actSettings: BriocheSettings = {
        ...this.activeSettings,
        classPositionActive: ""
      };
      this.root.render(<Brioche key={this.key} settings={actSettings} />);
      this.showTimer("Brioche timer / animate out");
      setTimeout(this.createFinalizerFn(), actSettings.animationTimeout);
    };
  }

  private dismissFn(dismissClass?: string) {
    if (!this.activeSettings) {
      throw new Error("No active settings are present");
    }
    // Moving out animation started, this Brioche is not dismissable anymore
    this.isDismissable = false;
    const actSettings: BriocheSettings = {
      ...this.activeSettings,
      classPositionActive: `${dismissClass ? this.activeSettings.classPositionActive : ""} ${dismissClass ?? ""}`
    };
    this.root.render(<Brioche key={this.key} settings={actSettings} />);
    this.showTimer("Brioche timer / dismissed");
    setTimeout(this.createFinalizerFn(), actSettings.animationTimeout);
  }

  /**
   * This function removes the Brioche from the DOM at the end of the deactivation animation
   * @private
   */
  private createFinalizerFn() {
    return () => {
      if (!this.activeSettings) {
        throw new Error("No active settings are present");
      }
      this.isDismissable = false;
      const actSettings: BriocheSettings = {
        ...this.activeSettings,
        classVisible: "",
        classPositionActive: "",
        classPosition: ""
      };
      this.root.render(<Brioche key={this.key} settings={actSettings} />);
      this.showTimer("Brioche timer / finalize");
      setTimeout(() => {
        this.isRendering = false;
        this.render();
      }, this.activeSettings.animationTimeout);
    };
  }

  /**
   * Sets some CSS variables dynamically, so the Brioche messages could follow changes in the page.
   * E.g.: Member navigates to Chat tab
   * @param settings
   */
  private readonly setCssVars = (settings: BriocheSettings) => {
    // Set the position of the wrapper
    this.rootElem.classList.remove(...this.rootElem.classList.values());
    this.rootElem.classList.add(settings.classPosition);

    // Set root variables
    this.rootElem.style.setProperty(
      "--animation-timeout",
      `${settings.animationTimeout}ms`
    );
    this.rootElem.style.setProperty(
      "--z-index-value",
      settings.zIndex.toString()
    );
  };

  /**
   * Shows the next Brioche message (if exists), and registers the cleanup functions
   */
  private readonly render = () => {
    if (this.isRendering) {
      return;
    }
    this.timerCheck = performance.now();
    const settings = this.queue.shift();
    if (!settings) {
      return;
    }
    this.activeSettings = settings;
    this.placeRootIntoWrapper();
    this.isRendering = true;
    const preRenderSettings: BriocheSettings = {
      ...settings,
      classPositionActive: ""
    };
    this.setCssVars(settings);
    // Adding Brioche to the DOM
    this.root.render(<Brioche key={this.key} settings={preRenderSettings} />);
    requestAnimationFrame(() => {
      this.isDismissable = true;
      this.timeoutsToBeDismissed.push(
        window.setTimeout(() => {
          this.showTimer(
            `Brioche timer / starting animation in / animation timeout: ${settings.animationTimeout.toFixed(1)}ms / active timeout ${settings.timeout.toFixed(1)}ms:`
          );
          // Starting animation and registering cleanup
          this.root.render(<Brioche key={this.key} settings={settings} />);
          setTimeout(
            this.createDeactivatorFn(),
            settings.timeout + settings.animationTimeout
          );
        })
      );
    });
  };

  /**
   * Puts a message to the end of the queue.
   * @param args
   */
  public show(args: BriocheArgs): void {
    // Let's prevent memory leaks
    if (this.queue.length > 100) {
      // Some error handling would be nice here. Let's wait while we exceed
      // the startup stage and could allocate enough time to do things properly...
      this.queue.shift();
    }
    this.addToQueue(this.argsToSettings(args));
  }

  /**
   * Themed alias of `show()`
   * @param args
   */
  public success(args: Omit<BriocheArgs, "theme">) {
    this.show({ ...args, theme: "success" });
  }

  /**
   * Themed alias of `show()`
   * @param args
   */
  public error(args: Omit<BriocheArgs, "theme">) {
    this.show({ ...args, theme: "error" });
  }

  /**
   * Dismisses a Brioche immediately
   */
  public dismiss(direction?: SwipeDirection): void {
    if (!this.isDismissable) {
      return;
    }
    this.timeoutsToBeDismissed.forEach(clearTimeout);
    switch (direction) {
      case "left":
        this.dismissFn("dismiss-left");
        break;
      case "right":
        this.dismissFn("dismiss-right");
        break;
      default:
        this.dismissFn();
    }
  }

  /**
   * Sets the Brioche debug mode.
   * Could be beneficial for checking timeouts and so.
   * @param value
   */
  public set debug(value: boolean) {
    this._debug = value;
  }
}

// Singleton instance
export const brioche = new BriocheEngine();
